import { RadioGroup } from "@headlessui/react";
import { ReactElement } from "react";
import { FCWithClassName } from "types";

import { Mastercard, Postfinance, Twint, Visa } from "@/assets/icons/icons";
import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { DrCreditCardCardType } from "@/services/model";

/** Bare provider icons, sized by the consumer - not laid out for any one picker. */
export const paymentProviderIcons: Record<DrCreditCardCardType, ReactElement> =
  {
    TWI: <Twint />,
    ECA: <Mastercard />,
    VIS: <Visa />,
    PFC: <Postfinance />,
  };

export const paymentProviderLabels: Record<DrCreditCardCardType, string> = {
  TWI: "TWINT",
  ECA: "Mastercard",
  VIS: "Visa",
  PFC: "PostFinance",
};

// Display order follows the labels map, not the generated enum.
const paymentProviders: PaymentOption[] = (
  Object.keys(paymentProviderLabels) as DrCreditCardCardType[]
).map((provider) => ({
  label: paymentProviderLabels[provider],
  value: provider,
  icon: paymentProviderIcons[provider],
}));

interface PaymentOption {
  label: string;
  subtext?: string;
  value: string;
  icon?: ReactElement;
  disabled?: boolean;
}

interface Props {
  /** Names the radio group (the payment-method prompt it sits under). */
  ariaLabel: string;
  paymentOptions?: PaymentOption[];
  selected?: string | undefined;
  onChange?: (s: DrCreditCardCardType) => void;
  /**
   * `"inline"` (default): icon sits directly between the radio and the label,
   * per the `dire-radio` Figma spec.
   * `"trailing"`: icon pushed to the far right of the row - legacy layout.
   */
  iconPlacement?: "trailing" | "inline";
}

/**
 * Displays a choice of payment providers with radio buttons.
 */
export const PaymentProviderPicker: FCWithClassName<Props> = ({
  ariaLabel,
  paymentOptions = paymentProviders,
  selected,
  onChange,
  className,
  iconPlacement = "inline",
}) => (
  <RadioGroup
    id="payment-provider-picker"
    className={className}
    aria-label={ariaLabel}
    value={selected || ""}
    onChange={(s: string) => s && onChange?.(s as DrCreditCardCardType)}
  >
    {paymentOptions.map(({ label, value, icon, disabled, subtext }) => (
      <div key={value} data-testid={`payment-option-${value}`} className="mb-6">
        {iconPlacement === "inline" ? (
          <RadioButtonOption
            disabled={disabled}
            value={value}
            name="paymentOption"
            label={label}
            icon={icon}
          />
        ) : (
          <div className="flex items-center justify-between">
            <RadioButtonOption
              disabled={disabled}
              value={value}
              name="paymentOption"
              label={label}
            />
            <div>{icon}</div>
          </div>
        )}
        {subtext && (
          <div className="ml-7 mt-2 text-body-xs text-foreground-primary">
            {subtext}
          </div>
        )}
      </div>
    ))}
  </RadioGroup>
);
